import React, { memo, useCallback, useEffect, useState } from 'react'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { makeStyles } from 'tss-react/mui'
import { useDispatch } from 'react-redux'
import { Security } from '../../proto/generated/security'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { getCandles } from '../../services/apis/candles'
import { getSecurityKey } from '../../util/security'
import { SeasonalYearSeries, buildSeasonalsData } from '../../util/seasonals'
import { setSeasonalsPanelOpen } from '../../reducer/home.reducer'
import SeasonalsChart from './SeasonalsChart'

type SeasonalsSectionProps = {
    security: Security | undefined
}

/** ~3.5 years of trading days, enough to cover the current year plus the previous two full years. */
const SEASONALS_BACKWARD_CANDLE_COUNT = 900

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: theme.spacing(1.5, 2, 2)
    },
    header: {
        fontSize: '13px',
        fontWeight: 600,
        marginBottom: theme.spacing(1)
    },
    legend: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'center',
        marginTop: theme.spacing(1)
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5)
    },
    legendDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0
    },
    legendLabel: {
        fontSize: '12px',
        color: theme.palette.text.secondary
    },
    moreButton: {
        marginTop: theme.spacing(1),
        textTransform: 'none',
        fontSize: '12px',
        fontWeight: 500,
        justifyContent: 'space-between'
    }
}))

const SeasonalsSection = ({ security }: SeasonalsSectionProps) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const [series, setSeries] = useState<SeasonalYearSeries[]>([])
    const [loading, setLoading] = useState<boolean>(false)

    const handleOpenSeasonals = useCallback(() => {
        dispatch(setSeasonalsPanelOpen(true))
    }, [dispatch])

    const securityKey = security ? getSecurityKey(security.exchange, security.code) : null

    useEffect(() => {
        if (!security || !securityKey) {
            setSeries([])
            return
        }

        let cancelled = false
        setLoading(true)
        setSeries([])

        ;(async () => {
            try {
                const response = await getCandles({
                    body: {
                        security: { exchange: security.exchange, code: security.code },
                        timeBucket: { timeSeries: TimeBucket_TimeSeries.DAY, value: 1 },
                        referenceTime: Date.now(),
                        forwardCandleCount: 0,
                        backwardCandleCount: SEASONALS_BACKWARD_CANDLE_COUNT,
                        dontGiveExtraCandles: false,
                        dontApplyCorporateActionAdjustments: false
                    }
                })
                if (cancelled) return
                setSeries(buildSeasonalsData(response.backwardCandles, { priceMultiplier: security.priceMultiplier ?? 100 }))
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching seasonals data', error)
                    setSeries([])
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [security, securityKey])

    if (!security) return null

    if (loading) {
        return (
            <Box className={classes.container}>
                <Typography className={classes.header}>Seasonals</Typography>
                <Skeleton variant="rounded" height={200} />
            </Box>
        )
    }

    if (series.length === 0) return null

    return (
        <Box className={classes.container}>
            <Typography className={classes.header}>Seasonals</Typography>
            <SeasonalsChart series={series} />
            <Box className={classes.legend}>
                {series.map((yearSeries) => (
                    <Box key={yearSeries.year} className={classes.legendItem}>
                        <Box className={classes.legendDot} sx={{ backgroundColor: yearSeries.color }} />
                        <Typography className={classes.legendLabel}>{yearSeries.year}</Typography>
                    </Box>
                ))}
            </Box>
            <Button
                fullWidth
                variant="outlined"
                color="inherit"
                size="small"
                className={classes.moreButton}
                endIcon={<ChevronRightIcon fontSize="small" />}
                onClick={handleOpenSeasonals}
            >
                More seasonals
            </Button>
        </Box>
    )
}

export default memo(SeasonalsSection)
